import {
  doc, runTransaction, Timestamp, serverTimestamp, deleteDoc, updateDoc, getDoc,
  collection, getDocs, query, where, arrayUnion,
} from "firebase/firestore";
import emailjs from "emailjs-com";
import { getEmailLogo } from "./emailLogo";

export const RESERVATION_TTL_MS = 2 * 60 * 1000; // 2 minutes

// ── Timezone-Safe Date Utilities & Constraints ──────────────────────────────
export const getTodayLocal = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const formatDateToISO = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
};

export const formatToAppDateString = (dateOrStr) => {
  if (!dateOrStr) return "";
  let d;
  if (typeof dateOrStr === "string") {
    // If it's already in DD-MM-YYYY format, return as is
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateOrStr)) return dateOrStr;
    d = parseLocalDate(dateOrStr);
  } else {
    d = dateOrStr;
  }
  if (!d || isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

// ── Working-Day Helpers ───────────────────────────────────────────────────
// Returns a Set of holiday date strings ("YYYY-MM-DD") from the Firestore
// holidayDates array (supports both plain strings and { date, note } objects).
const buildHolidaySet = (holidayDates = []) =>
  new Set(
    holidayDates.map((h) => (typeof h === "object" ? h.date : h)).filter(Boolean)
  );

// Returns true if the given Date is a non-working day:
// a weekend (Sat/Sun) or a configured public holiday.
const isNonWorkingDay = (date, holidaySet) => {
  const day = date.getDay();
  if (day === 0 || day === 6) return true; // Sunday (0) or Saturday (6)
  return holidaySet.has(formatDateToISO(date));
};

/**
 * Advances from startDate day-by-day.
 * Tomorrow is Day 1 evaluated. If Monday application:
 * - Tuesday = 1st candidate day evaluated -> if valid working day, count = 1
 * - Wednesday = 2nd candidate day evaluated -> if valid working day, count = 2
 * - Thursday = 3rd candidate day evaluated -> if valid working day, count = 3
 * Returns the exact Nth valid working day.
 */
const addWorkingDays = (startDate, n, holidaySet) => {
  const pointer = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  let validWorkingDaysFound = 0;

  while (validWorkingDaysFound < n) {
    // Advance to the next calendar day
    pointer.setDate(pointer.getDate() + 1);

    // If it's a weekend or configured public holiday, skip without counting
    if (isNonWorkingDay(pointer, holidaySet)) {
      continue;
    }

    // It's a valid working day
    validWorkingDaysFound++;
  }

  return pointer;
};

/**
 * getMinSelectableDate(workingDays, holidayDates)
 *
 * Scans forward starting tomorrow. Counts valid working days (skipping weekends
 * and public holidays). Returns the exact Nth valid working day as the minimum limit.
 *
 * @param {number}  workingDays  - Number of valid working days (default 3)
 * @param {Array}   holidayDates - Array from Firestore settings/publicHolidays
 */
export const getMinSelectableDate = (workingDays = 3, holidayDates = []) => {
  const today = getTodayLocal();
  const holidaySet = buildHolidaySet(holidayDates);
  return addWorkingDays(today, workingDays, holidaySet);
};

/**
 * validateInterviewDate(dateStr, workingDays, holidayDates)
 *
 * Validates that:
 *  1. Date is selected and valid.
 *  2. Date is not a weekend (Sat/Sun).
 *  3. Date is not an admin-configured public holiday.
 *  4. Date is at or after the Nth valid working day.
 *
 * @param {string}  dateStr      - "YYYY-MM-DD"
 * @param {number}  workingDays  - Minimum working-day gap (default 3)
 * @param {Array}   holidayDates - Array from Firestore settings/publicHolidays
 */
export const validateInterviewDate = (dateStr, workingDays = 3, holidayDates = []) => {
  if (!dateStr) {
    return { valid: false, error: "Please select an interview date." };
  }

  const selectedDate = parseLocalDate(dateStr);
  if (!selectedDate || isNaN(selectedDate.getTime())) {
    return { valid: false, error: "Please enter a valid date." };
  }

  const holidaySet = buildHolidaySet(holidayDates);

  // Check the selected date is not itself a weekend or holiday
  const dayOfWeek = selectedDate.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return {
      valid: false,
      error: "Weekends (Saturday and Sunday) are not available for interviews.",
    };
  }
  if (holidaySet.has(formatDateToISO(selectedDate))) {
    return {
      valid: false,
      error: "This date is a public holiday and is not available for interviews.",
    };
  }

  // Check the minimum working-day gap from today
  const minDate = getMinSelectableDate(workingDays, holidayDates);
  if (selectedDate < minDate) {
    return {
      valid: false,
      error: `You must reserve a slot at least ${workingDays} working days after your application date.`,
    };
  }

  return { valid: true };
};

// Persists across refresh and all tabs from the same browser (localStorage)
export const getOrCreateUserId = () => {
  try {
    let uid = localStorage.getItem("vbt_user_id");
    if (!uid) {
      uid = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("vbt_user_id", uid);
    }
    return uid;
  } catch {
    return Math.random().toString(36).slice(2);
  }
};

// Unique per browser tab; survives refresh within the same tab (sessionStorage)
export const getOrCreateTabId = () => {
  try {
    let tid = sessionStorage.getItem("vbt_tab_id");
    if (!tid) {
      tid = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("vbt_tab_id", tid);
    }
    return tid;
  } catch {
    return Math.random().toString(36).slice(2);
  }
};

// Deterministic doc ID — global across all job/opportunity applications for the same slot.
export const buildReservationDocId = (date, slotTime) =>
  `${date}_${slotTime.replace(/[: ]/g, "-")}`;

export const createSlotReservation = async (db, date, slotTime, userId, tabId, holidayDates = []) => {
  const dateValidation = validateInterviewDate(date, 3, holidayDates);
  if (!dateValidation.valid) {
    throw new Error(dateValidation.error);
  }

  const docId = buildReservationDocId(date, slotTime);
  const reservationRef = doc(db, "slotReservations", docId);
  const now = new Date();
  const expiresAt = new Date(now.getTime() + RESERVATION_TTL_MS);

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(reservationRef);

    if (snap.exists()) {
      const data = snap.data();
      const expired = data.expiresAt.toDate() <= now;
      // Lock ownership is per-tab, not per-user — two tabs of the same user are independent
      const isThisTab = data.tabId === tabId;

      if (data.status === "confirmed") throw new Error("SLOT_PERMANENTLY_TAKEN");
      // Block any tab that didn't create the lock, even if same userId
      if (!expired && !isThisTab) throw new Error("SLOT_TEMPORARILY_RESERVED");

      // This tab's own lock (or an expired one) — refresh/claim it
      transaction.update(reservationRef, {
        userId,
        tabId,
        status: "temporary",
        expiresAt: Timestamp.fromDate(expiresAt),
        updatedAt: serverTimestamp(),
      });
    } else {
      transaction.set(reservationRef, {
        interviewDate: date,
        slotTime,
        userId,
        tabId,
        status: "temporary",
        expiresAt: Timestamp.fromDate(expiresAt),
        createdAt: serverTimestamp(),
      });
    }
  });

  return { docId, expiresAt };
};

export const releaseReservation = (db, docId) =>
  deleteDoc(doc(db, "slotReservations", docId)).catch(() => {});

export const confirmReservation = (db, docId) =>
  updateDoc(doc(db, "slotReservations", docId), {
    status: "confirmed",
    confirmedAt: serverTimestamp(),
  }).catch(() => {});

// Restore an existing reservation for this user (any tab).
export const restoreReservation = async (db, docId, userId, tabId) => {
  try {
    const snap = await getDoc(doc(db, "slotReservations", docId));
    if (!snap.exists()) return null;
    const data = snap.data();
    const expiresAt = data.expiresAt.toDate();
    if (data.userId !== userId || data.status !== "temporary" || expiresAt <= new Date()) return null;
    // Hand the lock to the current tab
    await updateDoc(doc(db, "slotReservations", docId), { tabId, updatedAt: serverTimestamp() });
    return { ...data, expiresAt, docId };
  } catch {
    return null;
  }
};

// ── Interview Rescheduling — Data Layer ───────────────────────────────────

/**
 * lookupApplication(db, cnic, email)
 *
 * Searches both `opportunityApplications` and `jobApplications` collections
 * for an active application matching the given CNIC and email (case-insensitive
 * on email via lowercase normalisation at write-time; compared as stored here).
 *
 * Returns:
 *   { doc: DocumentSnapshot, collectionName: string }  — if found in either collection
 *   null                                               — if no match exists
 *
 * Search order: opportunityApplications first, then jobApplications.
 * If multiple matches exist the most-recently created document wins.
 */
export const lookupApplication = async (db, cnic, email) => {
  if (!cnic || !email) return null;

  const normalisedEmail = email.trim().toLowerCase();
  const collections = ["opportunityApplications", "jobApplications"];

  for (const collectionName of collections) {
    try {
      // Primary: match on both CNIC and email
      const q = query(
        collection(db, collectionName),
        where("cnic", "==", cnic.trim()),
        where("email", "==", normalisedEmail)
      );
      const snap = await getDocs(q);

      if (!snap.empty) {
        // Pick the most recently created doc if multiple somehow exist
        const sorted = snap.docs.sort((a, b) => {
          const tA = a.data().createdAt?.toDate?.()?.getTime() ?? 0;
          const tB = b.data().createdAt?.toDate?.()?.getTime() ?? 0;
          return tB - tA;
        });
        return { doc: sorted[0], collectionName };
      }
    } catch (err) {
      console.error(`[lookupApplication] Error querying ${collectionName}:`, err);
    }
  }

  return null;
};

/**
 * rescheduleSlotReservation(db, appDocId, collectionName, oldSlot, newDate, newSlot, reason)
 *
 * Atomically:
 *   1. Releases the previous slot reservation document from `slotReservations`.
 *   2. Updates the application document with new date, slot, status, and a full
 *      audit trail in `rescheduleHistory`.
 *
 * @param {object} db             - Firestore db instance
 * @param {string} appDocId       - ID of the application document to update
 * @param {string} collectionName - "opportunityApplications" | "jobApplications"
 * @param {string} oldSlot        - Previous slot doc ID (from buildReservationDocId) — released if present
 * @param {string} newDate        - New interview date in "YYYY-MM-DD" or "DD-MM-YYYY" format
 * @param {string} newSlot        - New slot time label (e.g. "10:00 AM - 11:00 AM")
 * @param {string} reason         - Admin / candidate-provided reschedule reason
 *
 * @returns {Promise<void>}
 * @throws  {Error} if the Firestore update fails
 */
export const rescheduleSlotReservation = async (
  db,
  appDocId,
  collectionName,
  oldSlot,
  newDate,
  newSlot,
  reason
) => {
  // 1. Release the previous slot reservation (best-effort — don't block on failure)
  if (oldSlot) {
    await releaseReservation(db, oldSlot);
  }

  // 2. Normalise the new date to the app's DD-MM-YYYY storage format
  const normalisedNewDate = formatToAppDateString(newDate);

  // 3. Capture the old values from the application document for history
  const appRef = doc(db, collectionName, appDocId);
  const currentSnap = await getDoc(appRef);
  if (!currentSnap.exists()) {
    throw new Error(`Application document ${appDocId} not found in ${collectionName}.`);
  }
  const currentData = currentSnap.data();
  const oldDate = currentData.interviewDate ?? null;
  const oldSlotTime = currentData.interviewSlot ?? currentData.interviewTime ?? null;

  // 4. Build the history entry
  // NOTE: serverTimestamp() is a Firestore sentinel and cannot be nested inside
  // arrayUnion(). Use a plain ISO string timestamp for the history record instead.
  const historyEntry = {
    oldDate,
    oldSlot: oldSlotTime,
    newDate: normalisedNewDate,
    newSlot,
    reason: reason?.trim() ?? "",
    updatedAt: new Date().toISOString(),
  };

  // 5. Write the update to the application document
  // serverTimestamp() is valid at the top-level document field level
  await updateDoc(appRef, {
    interviewDate: normalisedNewDate,
    interviewSlot: newSlot,
    status: "rescheduled",
    rescheduleReason: reason?.trim() ?? "",
    rescheduleHistory: arrayUnion(historyEntry),
    statusUpdatedAt: serverTimestamp(),
  });
};

const escapeEmailHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const sendRescheduleEmail = async (params) => {
  emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
  return emailjs.send(
    process.env.REACT_APP_EMAILJS_SERVICE_ID,
    process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
    params,
    process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
  );
};

const buildRescheduleEmail = ({
  heading,
  greeting,
  intro,
  details,
  footer,
  emailLogo,
  checklist = [],
}) => {
  const rows = details
    .map(
      ([label, value]) =>
        `<tr><td style="padding:8px 0;color:#888;font-size:13px;width:38%;border-bottom:1px solid #f5e6d0;">${escapeEmailHtml(label)}</td><td style="padding:8px 0;color:#111;font-size:13px;font-weight:600;border-bottom:1px solid #f5e6d0;">${escapeEmailHtml(value || "N/A")}</td></tr>`,
    )
    .join("");
  const checklistRows = checklist
    .map(
      ({ title, description }) =>
        `<tr><td style="padding:6px 0;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#fff8f0;border:1px solid #ffd199;border-radius:10px;"><tr><td style="padding:13px 16px;"><p style="margin:0 0 3px;font-weight:700;color:#ff6600;font-size:13px;">${escapeEmailHtml(title)}</p><p style="margin:0;color:#555;font-size:13px;line-height:1.6;">${description}</p></td></tr></table></td></tr>`,
    )
    .join("");
  const checklistSection = checklistRows
    ? `<tr><td style="padding:8px 32px 16px;"><p style="margin:0 0 12px;font-size:11px;font-weight:700;color:#999;text-transform:uppercase;letter-spacing:1.2px;">Before You Arrive</p><table width="100%" cellpadding="0" cellspacing="0">${checklistRows}</table></td></tr>`
    : "";

  return `<table width="100%" cellpadding="0" cellspacing="0" style="background:#f0f4f8;padding:36px 0;font-family:Segoe UI,Arial,sans-serif;"><tr><td align="center"><table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:16px;overflow:hidden;"><tr><td style="background:#ff8000;text-align:center;padding:36px 24px 30px;"><img src="${escapeEmailHtml(emailLogo)}" width="160" alt="VBT" style="display:block;margin:0 auto 18px;" /><h1 style="margin:0;color:#ffffff;font-size:24px;font-weight:700;">${escapeEmailHtml(heading)}</h1><p style="margin:8px 0 0;color:rgba(255,255,255,0.85);font-size:14px;">VisionBird Technologies Careers Portal</p></td></tr><tr><td style="padding:32px 32px 16px;"><p style="margin:0 0 12px;font-size:16px;color:#222;line-height:1.7;">${greeting}</p><p style="margin:0;font-size:14px;color:#555;line-height:1.8;">${intro}</p></td></tr><tr><td style="padding:8px 32px 16px;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#fff8f0;border-radius:10px;border:1px solid #ffd199;"><tr><td style="padding:4px 0 4px 14px;width:4px;background:#ff8000;border-radius:10px 0 0 10px;"></td><td style="padding:14px 16px;"><p style="margin:0;color:#b35900;font-size:13px;font-weight:600;">Interview Schedule Update</p><p style="margin:4px 0 0;color:#804000;font-size:13px;">Please keep the details below for your records.</p></td></tr></table></td></tr><tr><td style="padding:8px 32px 16px;"><table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #eeeeee;border-radius:10px;overflow:hidden;">${rows}</table></td></tr>${checklistSection}<tr><td style="padding:8px 32px 28px;border-top:1px solid #f5f5f5;"><p style="margin:0;font-size:14px;color:#555;line-height:1.8;">${footer}</p><p style="margin:14px 0 0;font-size:14px;color:#333;">Warm Regards,<br /><strong style="color:#ff6600;">VisionBird Technologies &mdash; HR Team</strong></p></td></tr><tr><td style="background:#111111;text-align:center;padding:22px;"><p style="margin:0 0 4px;color:#ff8000;font-size:13px;font-weight:700;letter-spacing:0.5px;">VISIONBIRD TECHNOLOGIES</p><p style="margin:0;color:#666;font-size:12px;">&#169; ${new Date().getFullYear()} &nbsp;&#183;&nbsp; Please do not reply to this email</p></td></tr></table></td></tr></table>`;
};

export const sendAdminRescheduleEmail = async ({
  application,
  oldDate,
  oldSlot,
  newDate,
  newSlot,
  reason,
}) => {
  const logo = await getEmailLogo();
  const position = application.position || application.jobTitle || application.opportunityTitle || application.selectedField || "N/A";
  const adminEmail = process.env.REACT_APP_HR_EMAIL || process.env.REACT_APP_ADMIN_EMAIL;
  if (!adminEmail) throw new Error("HR email is not configured.");

  return sendRescheduleEmail({
    to_email: adminEmail,
    email: adminEmail,
    subject: `Interview Rescheduled: ${application.name || "Candidate"}`,
    message: buildRescheduleEmail({
      heading: "Interview Rescheduled",
      greeting: `Hello HR Team,`,
      intro: `<strong>${escapeEmailHtml(application.name || "Candidate")}</strong> has rescheduled an interview.`,
      details: [
        ["Candidate", application.name],
        ["Email", application.email],
        ["CNIC", application.cnic],
        ["Position", position],
        ["Previous date", oldDate],
        ["Previous slot", oldSlot],
        ["New date", newDate],
        ["New slot", newSlot],
        ["Candidate reason", reason],
      ],
      footer: "Please review the updated schedule in the admin panel.",
      emailLogo: logo,
    }),
  });
};

export const sendCandidateRescheduleEmail = async ({
  application,
  newDate,
  newSlot,
}) => {
  const recipient = (application.email || "").trim();
  if (!recipient) throw new Error("Candidate email is missing.");
  const logo = await getEmailLogo();
  const position = application.position || application.jobTitle || application.opportunityTitle || application.selectedField || "N/A";

  return sendRescheduleEmail({
    to_email: recipient,
    email: recipient,
    subject: "Your Interview Has Been Rescheduled | VisionBird Technologies",
    message: buildRescheduleEmail({
      heading: "Interview Rescheduled",
      greeting: `Hello <strong style="color:#ff6600;">${escapeEmailHtml(application.name || "Candidate")}</strong>,`,
      intro: `Your interview for <strong>${escapeEmailHtml(position)}</strong> has been successfully rescheduled. Please keep these updated details handy.`,
      details: [
        ["Interview date", newDate],
        ["Time slot", newSlot],
        ["Position", position],
      ],
      footer: "Before you arrive: be at least 10 minutes early, bring your original CNIC or government-issued ID, and bring a fully charged laptop for any assessment.",
      emailLogo: logo,
      checklist: [
        { title: "Be On Time", description: "Please arrive at least <strong>10 minutes early</strong>." },
        { title: "Bring Your ID Card", description: "Carry your original <strong>CNIC or valid government-issued ID</strong>." },
        { title: "Bring Your Laptop", description: "Bring a <strong>fully charged laptop</strong> for any assessment or practical task." },
      ],
    }),
  });
};
